import { v4 as uuid } from 'uuid';
import { isFunction, throttle } from 'lodash';

import { ApiError, ApiResponse } from '@packages/models/api';

import { StorageService, StorageKeys } from '../storage';
import { ConfigService, Constants } from '../config';
import { LogLevel, getLogger } from '../logging';
import jwt_decode from 'jwt-decode';

// Debug threshold keeps LOG_HTTP_REQUESTS independent of the global LOG_LEVEL.
const httpLogger = getLogger('http', { minLevel: LogLevel.Debug });

interface CustomHeaders {
    'X-Simulate-Delay'?: string;
    'X-Random-Error-Probability'?: string;
}

export enum ApiTarget {
    StsBackend = 'STS_BACKEND',
    VinService = 'VIN_SERVICE',
    OcrService = 'OCR_SERVICE',
}

export interface HttpHeaders {
    [headerName: string]: string;
}

export type TokenGetter = () => Promise<string | null>;

export interface HttpClientOptions {
    defaultHeaders: HttpHeaders;
    tokenGetter: TokenGetter;
}

export interface AuthTokenPayload {
    sub: string;
    exp: number;
    iat?: number;
    /** Epoch seconds after which the token is no longer eligible for refresh. */
    refreshExp?: number;
    soaUserName?: string;
    /**
     * Full account profile serialized as JSON, mirroring the `GET /accounts/{id}`
     * response. Present on tokens issued after the capabilities-in-JWT rollout;
     * consumers must tolerate its absence and fall back to the accounts API.
     */
    account?: string;
}

export interface TokenReceipt {
    iat: number;
    receivedAt: number;
}

const REFRESH_RETRY_DELAY_MS = 30_000;

export function createTokenReceipt(payload: AuthTokenPayload): TokenReceipt | null {
    return payload.iat === undefined ? null : { iat: payload.iat, receivedAt: Date.now() };
}

export function secondsUntilExpiry(payload: AuthTokenPayload, receipt: TokenReceipt | null): number {
    const byClock = payload.exp - Date.now() / 1000;

    if (payload.iat === undefined || !receipt || receipt.iat !== payload.iat) {
        return byClock;
    }

    const byLifetime = payload.exp - payload.iat - (Date.now() - receipt.receivedAt) / 1000;

    return Math.min(byClock, byLifetime);
}

function refreshThresholdInSeconds(payload: AuthTokenPayload): number {
    return payload.iat === undefined ? Constants.RefreshTokenWindowInSeconds : (payload.exp - payload.iat) / 2;
}

function isRefreshRequest(config: RequestConfig) {
    return config.url.endsWith('/refresh-access-token');
}

export interface RequestConfig<T = {}> {
    requestId?: string;
    method: 'PUT' | 'POST' | 'GET' | 'DELETE' | 'PATCH';
    url: string;
    headers?: HttpHeaders & CustomHeaders;
    body?: T;
    signal?: AbortSignal;
    ignoreCache?: boolean;
    apiTarget?: ApiTarget;
    responseType?: 'json' | 'text' | 'blob';
}

export class HttpClient {
    private cache = new Map<string, any>();
    private defaultHeaders: HttpHeaders;
    private httpLoggingEnabled = true;
    private enableDebugApiUtils = false;
    private tokenGetter: TokenGetter;
    private freshTokenResolver: null | Promise<string | null> = null;
    private failedRefresh: null | { token: string; at: number } = null;
    private sessionTimeoutHandler: null | ((exp: number) => void) = null;
    private notAuthenticatedHandler: null | (() => void) = null;
    private tokenUpdateHandler: null | ((newToken: string) => void) = null;
    private timer?: number;

    private apiBaseUrls: Record<ApiTarget, string | null> = {
        [ApiTarget.StsBackend]: this.configService.env.API_BASE_URL || null,
        [ApiTarget.VinService]: this.configService.env.VIN_BASE_URL || null,
        [ApiTarget.OcrService]: this.configService.env.OCR_BASE_URL || null,
    };

    constructor(
        private storageService: StorageService,
        private configService: ConfigService,
        options: HttpClientOptions
    ) {
        this.tokenGetter = options.tokenGetter;
        this.defaultHeaders = options.defaultHeaders;

        this.refreshToken = throttle(this.refreshToken.bind(this), 30000) as unknown as () => Promise<
            string | undefined
        >;

        this.httpLoggingEnabled = configService.getBoolean(Constants.Env.LogHttpRequests, true);
        this.enableDebugApiUtils = configService.getBoolean(Constants.Env.DebugApiUtils);
    }

    private async verifyTokenFreshness(accessToken: string): Promise<string> {
        // httpClient is already "busy" refreshing the token.
        // let's wait for that to resolve and use the fresh token
        // before firing whatever request we're attempting to fire
        if (this.freshTokenResolver) {
            return (await this.freshTokenResolver) ?? accessToken;
        }

        let payload: AuthTokenPayload;
        try {
            payload = jwt_decode(accessToken);
        } catch (e) {
            // couldn't decode
            return accessToken;
        }

        const expiresAfterInSeconds = await this.secondsLeft(payload);

        this.updateTimeoutTimer(expiresAfterInSeconds);

        if (expiresAfterInSeconds >= refreshThresholdInSeconds(payload) || this.isRefreshCoolingDown(accessToken)) {
            return accessToken;
        }

        return (await this.refreshAccessToken(accessToken)) ?? accessToken;
    }

    private async secondsLeft(payload: AuthTokenPayload) {
        const receipt: TokenReceipt | null = await this.storageService.getItem(StorageKeys.AccessTokenReceivedAt);

        return secondsUntilExpiry(payload, receipt);
    }

    private isRefreshCoolingDown(accessToken: string) {
        return this.failedRefresh?.token === accessToken && Date.now() - this.failedRefresh.at < REFRESH_RETRY_DELAY_MS;
    }

    private refreshAccessToken(accessToken: string): Promise<string | null> {
        if (this.freshTokenResolver) {
            return this.freshTokenResolver;
        }

        this.freshTokenResolver = this.orchestrateRequest<{ accessToken: string }>({
            method: 'POST',
            url: '/accounts/refresh-access-token',
            body: { accessToken },
        })
            .then(async (response) => {
                if (response.success) {
                    try {
                        await this.saveRefreshedToken(response.data.accessToken);
                        return response.data.accessToken;
                    } catch (error) {
                        httpLogger.error('Unable to save the refreshed access token', { error });
                    }
                }

                this.failedRefresh = { token: accessToken, at: Date.now() };
                return null;
            })
            .finally(() => {
                this.freshTokenResolver = null;
            });

        return this.freshTokenResolver;
    }

    private async saveRefreshedToken(newToken: string) {
        const payload: AuthTokenPayload = jwt_decode(newToken);
        const receipt = createTokenReceipt(payload);

        if (receipt) {
            await this.storageService.setItem(StorageKeys.AccessTokenReceivedAt, receipt);
        } else {
            await this.storageService.removeItem(StorageKeys.AccessTokenReceivedAt);
        }
        await this.storageService.setItem(StorageKeys.AccessToken, newToken);

        this.updateTimeoutTimer(secondsUntilExpiry(payload, receipt));

        this.tokenUpdateHandler?.(newToken);
    }

    private async recoverFromUnauthorized(sentToken: string | null) {
        let storedToken: string | null;
        try {
            storedToken = await this.tokenGetter();
        } catch {
            return false;
        }

        if (storedToken && storedToken !== sentToken) {
            return true;
        }

        if (!sentToken || this.isRefreshCoolingDown(sentToken)) {
            return false;
        }

        return (await this.refreshAccessToken(sentToken)) !== null;
    }

    private async request<T>(config: RequestConfig, startedAt: number, isRetry = false): Promise<T> {
        const { method = 'GET', headers = {}, signal, apiTarget, responseType } = config;

        let dynamicHeaders: HttpHeaders & CustomHeaders = {};
        let sentToken: string | null = null;

        if (isFunction(this.tokenGetter)) {
            try {
                let token = await this.tokenGetter();

                if (token) {
                    // skip verification for the refresh call
                    token = isRefreshRequest(config) ? token : await this.verifyTokenFreshness(token);
                    dynamicHeaders[Constants.AccessTokenHeaderName] = token;
                    sentToken = token;
                }
            } catch (e) {
                httpLogger.error('Unable to get an access token from storage', { error: e });
                throw new Error('TODO: Unable to get access token from storage');
            }
        }

        if (config.requestId) {
            dynamicHeaders[Constants.RequestIdHeaderName] = config.requestId;
        }

        if (this.enableDebugApiUtils) {
            const [apiRandomErrorProbability, apiSimulateDelay] = await Promise.all([
                this.storageService.getItem(StorageKeys.ApiRandomErrorProbability),
                this.storageService.getItem(StorageKeys.ApiSimulateDelay),
            ]);

            dynamicHeaders[Constants.RandomErrorProbabilityHeaderName] = apiRandomErrorProbability;
            dynamicHeaders[Constants.SimulateDelayHeaderName] = apiSimulateDelay;
        }

        const requestHeaders = Object.assign({}, this.defaultHeaders, dynamicHeaders, headers);

        if (config.body instanceof FormData) {
            // remove default content-type of application/json
            delete requestHeaders['content-type'];
        }

        const requestInit: RequestInit = {
            method,
            headers: requestHeaders,
            signal,
        };

        if (config.body) {
            requestInit.body = config.body instanceof FormData ? config.body : JSON.stringify(config.body);
        }

        if (this.httpLoggingEnabled) {
            httpLogger.debug(`Request started: [${method}] ${config.url}`, {
                method,
                requestId: config.requestId,
                url: config.url,
            });
        }

        // signals could abort while performing
        // other async tasks
        // such as refreshing access token
        if (config.signal && config.signal.aborted) {
            const err = new Error('Request Aborted');
            err.name = 'AbortError';
            throw err;
        }

        const baseUrl = this.apiBaseUrls[apiTarget ?? ApiTarget.StsBackend];
        if (!baseUrl) {
            throw new Error(`Base URL not configured for API target: ${apiTarget ?? ApiTarget.StsBackend}`);
        }
        const url = `${baseUrl}${config.url}`;
        const response = await fetch(url, requestInit);

        if (
            response.status === 401 &&
            !isRetry &&
            !isRefreshRequest(config) &&
            (await this.recoverFromUnauthorized(sentToken))
        ) {
            return this.request<T>(config, startedAt, true);
        }

        const parsedResponse = await this.parseResponse<T>(response, responseType, !isRefreshRequest(config));

        if (this.httpLoggingEnabled) {
            const durationMs = Date.now() - startedAt;
            httpLogger.debug(
                `Request completed: [${method}] ${config.url} returned [${response.status}] after [${durationMs}ms]`,
                {
                    durationMs,
                    method,
                    requestId: config.requestId,
                    statusCode: response.status,
                    url: config.url,
                }
            );
        }

        if (method === 'GET' && !config.ignoreCache) {
            this.cache.set(config.url, parsedResponse);
        }

        return parsedResponse;
    }

    private async parseResponse<T>(
        response: Response,
        responseType?: 'json' | 'text' | 'blob',
        handleUnauthenticated = true
    ) {
        if (response.status === 204) {
            return {} as T;
        }

        const body = await readBody(response, responseType);

        // Standardized Northstar response to match our ApiResponse<T>
        if (body.statusCode !== undefined && body.statusCode >= 400) {
            const message = body.message || 'Unexpected error from API';
            const rawStatus = body.status || 'API_ERROR';
            return Promise.reject({
                code: rawStatus.replace(/\s+/g, '_').toUpperCase(),
                message,
            } as ApiError);
        }

        if (!response.ok) {
            if (this.notAuthenticatedHandler && response.status === 401 && handleUnauthenticated) {
                this.notAuthenticatedHandler();
            }

            return Promise.reject(body as ApiError);
        }

        return body as T;
    }

    private updateTimeoutTimer(tokenExpirationInSeconds: number) {
        if (this.timer) {
            clearTimeout(this.timer);
        }

        const startTiming = Date.now() / 1000;
        this.timer = window.setTimeout(
            () => {
                this.sessionTimeoutHandler && this.sessionTimeoutHandler(startTiming + tokenExpirationInSeconds);
            },
            (tokenExpirationInSeconds - Constants.TimeoutPopupDurationInSeconds) * 1000
        );
    }

    get baseUrl() {
        return this.configService.env.API_BASE_URL;
    }

    get vinUrl() {
        return this.configService.env.VIN_BASE_URL;
    }

    async getFreshAccessToken(): Promise<string | undefined> {
        if (!isFunction(this.tokenGetter)) {
            return undefined;
        }
        const token = await this.tokenGetter();
        return token ? this.verifyTokenFreshness(token) : undefined;
    }

    registerSessionTimeoutHandler(handler: (exp: number) => void) {
        this.sessionTimeoutHandler = handler;
    }

    registerNotAuthenticatedHandler(handler: () => void) {
        this.notAuthenticatedHandler = handler;
    }

    registerTokenUpdateHandler(handler: (newToken: string) => void) {
        this.tokenUpdateHandler = handler;
    }

    queryString(params: { [param: string]: any }) {
        if (!params || Object.keys(params).length === 0) {
            return '';
        }

        return Object.entries(params).reduce((acc, [param, value]) => {
            if (value === undefined) {
                return acc;
            }

            acc += acc ? '&' : '?';

            acc += `${encodeURIComponent(param)}=${encodeURIComponent(value)}`;
            return acc;
        }, '');
    }

    async orchestrateRequest<T>(config: RequestConfig): Promise<ApiResponse<T>> {
        if (config.method === 'GET' && !config.ignoreCache) {
            const cachedResponse: T = this.cache.get(config.url);

            if (cachedResponse) {
                return Promise.resolve({
                    success: true,
                    fromCache: true,
                    data: cachedResponse,
                });
            }
        } else if (config.method !== 'GET') {
            this.cache.clear();
        }

        const requestId = uuid();
        config.requestId = requestId;
        const startedAt = Date.now();

        let abortHandler;
        if (config.signal) {
            abortHandler = () => {
                if (this.httpLoggingEnabled) {
                    const durationMs = Date.now() - startedAt;
                    httpLogger.debug(`Request aborted: [${config.method}] ${config.url} after [${durationMs}ms]`, {
                        durationMs,
                        method: config.method,
                        requestId,
                        url: config.url,
                    });
                }
            };
            config.signal.addEventListener('abort', abortHandler);
        }

        try {
            const data = await this.request<T>(config, startedAt);

            if (abortHandler) {
                config.signal!.removeEventListener('abort', abortHandler);
            }

            return {
                success: true,
                fromCache: false,
                data,
            };
        } catch (error) {
            const aborted = error instanceof Error && error.name === 'AbortError';

            // Request failures always log, independent of the LOG_HTTP_REQUESTS toggle.
            if (!aborted) {
                const durationMs = Date.now() - startedAt;
                const { code } = (error ?? {}) as Partial<ApiError>;
                httpLogger.error(`Request failed: [${config.method}] ${config.url} after [${durationMs}ms]`, {
                    code,
                    durationMs,
                    error,
                    method: config.method,
                    requestId,
                    url: config.url,
                });
            }

            return {
                success: false,
                aborted,
                data: error as ApiError,
            };
        }
    }

    refreshToken() {
        return this.storageService.getItem(StorageKeys.AccessToken).then((t) => {
            if (t) {
                return this.verifyTokenFreshness(t);
            }
        });
    }

    /**
     * Forces a token refresh regardless of how much time is left on the current token.
     * Use this for explicit user actions like "Extend Session" where we always want a fresh token.
     */
    async forceRefreshToken() {
        const accessToken: string | null = await this.storageService.getItem(StorageKeys.AccessToken);
        if (!accessToken) {
            throw new Error('No access token in storage');
        }

        const newToken = await this.refreshAccessToken(accessToken);
        if (!newToken) {
            throw new Error('Token refresh request failed');
        }

        return newToken;
    }

    async checkStoredToken() {
        const accessToken: string | null = await this.storageService.getItem(StorageKeys.AccessToken);
        if (!accessToken) {
            return;
        }

        const token = await this.verifyTokenFreshness(accessToken);
        if (token !== accessToken) {
            return;
        }

        let payload: AuthTokenPayload;
        try {
            payload = jwt_decode(token);
        } catch {
            return;
        }

        if ((await this.secondsLeft(payload)) <= 0) {
            this.notAuthenticatedHandler?.();
        }
    }

    clearCache() {
        this.cache.clear();
    }
}

/**
 * Read a response body as the caller expects it, answering an empty body when it cannot be read.
 * The status alone decides whether the request succeeded, so a body that is not the expected type
 * never turns a failed response into a successful one.
 *
 * @param response - the response to read
 * @param responseType - the body type the caller expects
 * @returns the body, or an empty one when it could not be read; rejects when the read is aborted
 */
async function readBody(response: Response, responseType?: 'json' | 'text' | 'blob') {
    try {
        if (responseType === 'blob') {
            return await response.blob();
        }

        if (responseType === 'text') {
            return await response.text();
        }

        return await response.json();
    } catch (error) {
        if (error instanceof Error && error.name === 'AbortError') {
            // abort signal can trigger
            // between making the request and calling "res.json()"
            throw error;
        }

        return {};
    }
}
